import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../../authToken';
import { API_BASE_URL } from '../../config';
import { calcRangeUtcMillis } from './monthlyTaskUtils';

const MONTHS = [
  { id: 1, label: 'jan' },
  { id: 2, label: 'feb' },
  { id: 3, label: 'mar' },
  { id: 4, label: 'apr' },
  { id: 5, label: 'may' },
  { id: 6, label: 'jun' },
  { id: 7, label: 'jul' },
  { id: 8, label: 'aug' },
  { id: 9, label: 'sep' },
  { id: 10, label: 'oct' },
  { id: 11, label: 'nov' },
  { id: 12, label: 'dec' }
];

export default function MonthlyTaskAdderPanel({
  onClose,
  onCancel,
  onAdd,
  initialMonth = null
}) {
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSelectedMonth(initialMonth);
  }, [initialMonth]);

  const buttonStyle = useMemo(() => ({
    padding: '12px 16px',
    border: '2px solid #6cf',
    borderRadius: '10px',
    backgroundColor: 'white',
    color: '#227',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 700,
    transition: 'all 0.15s',
    minHeight: '44px',
    textTransform: 'lowercase'
  }), []);

  const selectedButtonStyle = useMemo(() => ({
    ...buttonStyle,
    backgroundColor: '#6cf',
    color: '#123',
    transform: 'scale(0.97)'
  }), [buttonStyle]);

  const handleCancel = () => {
    if (typeof onCancel === 'function') return onCancel();
    if (typeof onClose === 'function') return onClose();
  };

  const handleAdd = async () => {
    if (!selectedMonth || isAdding) return;

    setIsAdding(true);
    setError(null);

    try {
      const now = new Date();
      const year = now.getUTCFullYear();

      const templatesRes = await fetch(`${API_BASE_URL}/monthlytasktemplates`, {
        headers: {
          ...(authToken ? { 'Authorization': authToken } : {})
        }
      });
      if (!templatesRes.ok) throw new Error('Failed to fetch monthly task templates');
      const templates = await templatesRes.json();
      const list = Array.isArray(templates) ? templates : [];

      // Create tasks sequentially to keep server load gentle and keep error handling simple.
      for (const t of list) {
        const name = (t?.name ?? '').toString().trim();
        const dayOfMonth = Number(t?.dayOfMonth);
        const duration = Number(t?.duration ?? 0);

        if (!name) continue;
        if (!Number.isInteger(dayOfMonth) || dayOfMonth === 0) continue;
        if (!Number.isInteger(duration) || duration < 0) continue;

        const { validFrom, validTo } = calcRangeUtcMillis({
          year,
          month: selectedMonth,
          dayOfMonth,
          duration
        });

        const res = await fetch(`${API_BASE_URL}/tasks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { 'Authorization': authToken } : {})
          },
          body: JSON.stringify({
            name,
            validFrom,
            validTo,
            done: false
          })
        });

        if (!res.ok) {
          throw new Error(`Failed to add task: ${name}`);
        }
      }

      if (typeof onAdd === 'function') {
        onAdd(selectedMonth);
      } else {
        handleCancel();
      }
    } catch (e) {
      setError(e?.message ?? String(e));
      setIsAdding(false);
      return;
    }

    setIsAdding(false);
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '24px 12px 18px 12px',
      boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      minWidth: 0,
      width: '520px',
      maxWidth: '96vw',
      minHeight: 0,
      maxHeight: '92vh',
      overflow: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <button
        onClick={handleCancel}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >✖️</button>

      <h2 style={{ marginBottom: 16 }}>Add monthly</h2>

      {error && (
        <div style={{
          color: '#a22',
          background: '#fff0f0',
          border: '1px solid #f66',
          borderRadius: 10,
          padding: 10,
          marginBottom: 12,
          fontWeight: 600
        }}>
          {error}
        </div>
      )}

      <div style={{
        padding: 16,
        background: '#f8f9fa',
        borderRadius: 12,
        border: '2px solid #e9ecef'
      }}>
        <div style={{
          margin: '0 0 10px 0',
          textAlign: 'center',
          color: '#227',
          fontSize: '14px',
          fontWeight: 800
        }}>
          Pick month
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridTemplateRows: 'repeat(3, 1fr)',
          gap: 10,
          maxWidth: 420,
          margin: '0 auto'
        }}>
          {MONTHS.map((m) => {
            const isSelected = selectedMonth === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setSelectedMonth(m.id)}
                style={isSelected ? selectedButtonStyle : buttonStyle}
                aria-pressed={isSelected}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        <div style={{
          marginTop: 12,
          textAlign: 'center',
          fontSize: 12,
          color: '#666',
          minHeight: 16
        }}>
          {selectedMonth ? `Selected: ${MONTHS.find((m) => m.id === selectedMonth)?.label}` : 'No month selected'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 700,
            borderRadius: 10,
            border: '1px solid #6cf',
            background: (selectedMonth && !isAdding) ? '#eaf6ff' : '#f2f2f2',
            color: (selectedMonth && !isAdding) ? '#227' : '#888',
            cursor: (selectedMonth && !isAdding) ? 'pointer' : 'not-allowed',
            transition: 'background 0.2s'
          }}
            disabled={!selectedMonth || isAdding}
          onClick={handleAdd}
        >
            {isAdding ? 'adding…' : '➕ add'}
        </button>
        <button
          style={{
            flex: 1,
            padding: '10px 0',
            fontSize: '1.1rem',
            fontWeight: 700,
            borderRadius: 10,
            border: '1px solid #ddd',
            background: '#fff',
            color: '#444',
            cursor: 'pointer'
          }}
          onClick={handleCancel}
          disabled={isAdding}
        >
          cancel
        </button>
      </div>
    </div>
  );
}
